import React, { ChangeEvent, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { CreatePendencyFormValues } from '../..'
import {
  PendencyDocInitialListEnum,
  PendencyDocInitialListEnumLocalized
} from 'common/enum/actions-pendency'
import { capitalizeEachWordText } from 'common/utils/capitalizeEachWordText'
import { greetingMessage } from 'presentation/utils/greetingMessage'
import CardInputMessage from '../../CardInputMessage'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import CheckboxNew from 'presentation/shared/components/CheckboxNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import { ReactComponent as DoubleArrowIcon } from 'presentation/assets/icons/double-arrow.svg'
import RMarkdown from 'presentation/hospital/components/RMarkdown'
import CardMessagePreview from '../../CardMessagePreview'
import * as S from '../styles'

type PendencyTabProps = {
  title: string
  handleCheckboxChange?: (event: ChangeEvent<HTMLInputElement>) => void
  isOptionChecked?: (doc: string) => boolean
}

const PendencyInitialDocumentTab = ({
  title,
  handleCheckboxChange,
  isOptionChecked
}: PendencyTabProps) => {
  const { values, isValid, handleChange, setFieldValue, errors } =
    useFormikContext<CreatePendencyFormValues>()

  let documentList = ''

  // Docs
  const docs =
    values.selected_options?.filter(
      (doc) => doc !== PendencyDocInitialListEnum.OTHERS
    ) || []

  docs?.forEach((doc) => {
    const optionName =
      PendencyDocInitialListEnumLocalized[
        doc as keyof typeof PendencyDocInitialListEnum
      ]
    documentList += `- **${optionName}**  \n`
  })

  // Others docs
  if (
    isOptionChecked?.(PendencyDocInitialListEnum.OTHERS) &&
    values.OPTION_OTHERS_TEXT
  ) {
    // 1. Remove espaços extras e quebras múltiplas
    // 2. Substitui quebra real por "\n" visível
    const othersTextNormalized = values.OPTION_OTHERS_TEXT?.split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length)
      .join('\\n')

    const othersTextArr = othersTextNormalized
      ?.split('\\n')
      ?.map((item: string) => `- **${item}**  \n`)

    othersTextArr.forEach((item) => {
      documentList += item
    })
  }

  const messageBody = [
    `Prezado(a) **Dr(a) ${capitalizeEachWordText(
      values?.doctor_name ?? '-'
    )}, ${greetingMessage?.()}** Informamos que após análise da assessoria técnica estão pendentes os seguintes documentos:`,
    `${documentList}`,
    `Reforçamos que para solicitar o pedido na operadora, faz imprescindível o envio destes.`
  ]

  const rawMessageText = messageBody.join('\n\n')

  useEffect(() => {
    setFieldValue('general_observations', rawMessageText)
  }, [values.selected_options, values.OPTION_OTHERS_TEXT])

  return (
    <S.TabBoxWrapper isValid={isValid}>
      <CardInputMessage title={title}>
        <SupportTextNew color="neutral900">
          Prezado(a) Dr(a){' '}
          <SupportTextNew as="span" color="neutral900" weight="bold">
            {values?.doctor_name
              ? capitalizeEachWordText(values?.doctor_name)
              : '-'}
          </SupportTextNew>
          ,{' '}
          <SupportTextNew as="span" color="neutral900" weight="bold">
            {greetingMessage?.()}
          </SupportTextNew>{' '}
          Informamos que após análise da assessoria técnica estão pendentes os
          seguintes documentos:
        </SupportTextNew>
        <S.CheckboxWrapper>
          {Array.from(Object.values(PendencyDocInitialListEnum)).map((doc) => (
            <CheckboxNew
              key={doc}
              label={PendencyDocInitialListEnumLocalized[doc]}
              labelFor={doc}
              name={doc}
              onChange={(e) => handleCheckboxChange?.(e)}
              checked={isOptionChecked?.(doc)}
              value={doc}
            />
          ))}
          {isOptionChecked?.(PendencyDocInitialListEnum.OTHERS) && (
            <TextAreaNew
              label="Documento"
              labelOverlapping
              labelSize="xxsmall"
              fontSize="xsmall"
              placeholder="Digite o nome do documento"
              required
              requiredColor="neutral900"
              name="OPTION_OTHERS_TEXT"
              value={values.OPTION_OTHERS_TEXT}
              onChange={handleChange}
              error={
                errors.OPTION_OTHERS_TEXT
                  ? errors.OPTION_OTHERS_TEXT
                  : undefined
              }
            />
          )}
        </S.CheckboxWrapper>
        <SupportTextNew color="neutral900">
          Reforçamos que para solicitar o pedido na operadora, faz
          imprescindível o envio destes.
        </SupportTextNew>
      </CardInputMessage>
      <DoubleArrowIcon className="double-arrow-icon" width={48} height={48} />
      <CardMessagePreview>
        {values.selected_options && values.selected_options.length > 0 ? (
          <RMarkdown rawText={rawMessageText} />
        ) : (
          <SupportTextNew color="neutral600" size="xsmall">
            Selecione uma ou mais opções ao lado e caso necessário adicione
            texto complementar para visualizar a prévia da mensagem que será
            enviada
          </SupportTextNew>
        )}
      </CardMessagePreview>
    </S.TabBoxWrapper>
  )
}

export default PendencyInitialDocumentTab
